/**
 * PlatformPacingCard — édite la cadence anti-bot d'une plateforme
 * (PR3 — 2026-05-10).
 *
 * Section collapsible affichée sous chaque carte plateforme dans le
 * `<DiffusionTab>`. Form RHF + Zod, footer collant "Enregistrer" qui
 * appelle PUT /api/settings/publishing-pacing/{platform}.
 *
 * Cas `max_per_day=0` (Etsy/Saatchi/Artfinder) : le form affiche le
 * placeholder explicite "Désactivée — no-op pour {platform}" pour ne
 * pas tromper l'utilisatrice avec des champs vides trompeurs. Elle
 * peut quand même bumper `max_per_day` à 1+ pour activer.
 *
 * Hot hours sont éditées via une textarea JSON pour MVP — un picker
 * visuel `<HotHoursPicker>` peut être ajouté en itération.
 */
import { useEffect, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { z } from 'zod'

import Button from '@/components/ui/Button'
import Field from '@/components/ui/Field'
import Input from '@/components/ui/Input'
import Textarea from '@/components/ui/Textarea'
import { usePublishingPacing, useUpsertPublishingPacing } from '@/hooks/useHumanizer'
import type { components } from '@/types/api-generated'

type Platform = components['schemas']['Platform']

const hotHourSchema = z.object({
  start: z.string().regex(/^\d{2}:\d{2}(:\d{2})?$/, 'Format HH:MM'),
  end: z.string().regex(/^\d{2}:\d{2}(:\d{2})?$/, 'Format HH:MM'),
  weight: z.number().int().min(1).max(10),
})

const formSchema = z.object({
  max_per_day: z.number().int().min(0).max(100),
  min_gap_minutes: z.number().int().min(0).max(10080),
  jitter_minutes: z.number().int().min(0).max(120),
  cross_platform_min_gap_minutes: z.number().int().min(0).max(1440),
  hot_hours_jsonb_text: z.string(),
})

type FormValues = z.infer<typeof formSchema>

interface PlatformPacingCardProps {
  platform: Platform
  platformLabel: string
}

function _serializeHotHours(blocks: components['schemas']['HotHourBlock'][]): string {
  if (!blocks || blocks.length === 0) return '[]'
  return JSON.stringify(
    blocks.map((b) => ({
      start: typeof b.start === 'string' ? b.start.slice(0, 5) : '',
      end: typeof b.end === 'string' ? b.end.slice(0, 5) : '',
      weight: b.weight,
    })),
    null,
    2,
  )
}

function _parseHotHours(text: string): components['schemas']['HotHourBlock'][] {
  const trimmed = text.trim()
  if (!trimmed || trimmed === '[]') return []
  const parsed = JSON.parse(trimmed) as Array<{
    start: string
    end: string
    weight: number
  }>
  // Validation côté client — laissera le BE refuser si shape KO.
  for (const b of parsed) {
    hotHourSchema.parse(b)
  }
  return parsed.map((b) => ({
    start: b.start.length === 5 ? `${b.start}:00` : b.start,
    end: b.end.length === 5 ? `${b.end}:00` : b.end,
    weight: b.weight,
  }))
}

export default function PlatformPacingCard({ platform, platformLabel }: PlatformPacingCardProps) {
  const pacingQuery = usePublishingPacing()
  const upsertMutation = useUpsertPublishingPacing()

  const current = pacingQuery.data?.items.find((p) => p.platform === platform)

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      max_per_day: 0,
      min_gap_minutes: 0,
      jitter_minutes: 0,
      cross_platform_min_gap_minutes: 0,
      hot_hours_jsonb_text: '[]',
    },
  })

  // Une seule resync par chargement (pattern hydrated ref pour éviter
  // setState-in-effect).
  const hydratedRef = useRef(false)
  useEffect(() => {
    if (hydratedRef.current || !current) return
    reset({
      max_per_day: current.max_per_day,
      min_gap_minutes: current.min_gap_minutes,
      jitter_minutes: current.jitter_minutes,
      cross_platform_min_gap_minutes: current.cross_platform_min_gap_minutes,
      hot_hours_jsonb_text: _serializeHotHours(current.hot_hours_jsonb ?? []),
    })
    hydratedRef.current = true
  }, [current, reset])

  const onSubmit = async (values: FormValues) => {
    let hotHours: components['schemas']['HotHourBlock'][]
    try {
      hotHours = _parseHotHours(values.hot_hours_jsonb_text)
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'JSON invalide'
      toast.error(`hot_hours_jsonb invalide : ${detail}`)
      return
    }

    try {
      await upsertMutation.mutateAsync({
        platform,
        payload: {
          max_per_day: values.max_per_day,
          min_gap_minutes: values.min_gap_minutes,
          jitter_minutes: values.jitter_minutes,
          cross_platform_min_gap_minutes: values.cross_platform_min_gap_minutes,
          hot_hours_jsonb: hotHours,
        },
      })
      toast.success(`Cadence ${platformLabel} enregistrée.`)
    } catch (err) {
      const detail = (err as { detail?: string }).detail ?? 'Erreur inconnue'
      toast.error(`Enregistrement impossible : ${detail}`)
    }
  }

  if (pacingQuery.isLoading) {
    return <p className="ui-text-muted">Chargement de la cadence…</p>
  }

  const isPacingDisabled = current?.max_per_day === 0

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="platform-pacing-card-form">
      {isPacingDisabled ? (
        <p className="ui-text-muted">
          Désactivée — no-op pour {platformLabel}. Marketplace sans feed algorithmique : la
          publication part au prochain tick scheduler sans humanizer. Bumpe <code>max_per_day</code>{' '}
          à 1+ pour activer.
        </p>
      ) : null}

      <div className="platform-pacing-card-grid">
        <Field label="Max par jour" error={errors.max_per_day?.message}>
          {(props) => (
            <Input type="number" {...register('max_per_day', { valueAsNumber: true })} {...props} />
          )}
        </Field>
        <Field label="Min gap (min)" error={errors.min_gap_minutes?.message}>
          {(props) => (
            <Input
              type="number"
              {...register('min_gap_minutes', { valueAsNumber: true })}
              {...props}
            />
          )}
        </Field>
        <Field label="Jitter (±min)" error={errors.jitter_minutes?.message}>
          {(props) => (
            <Input
              type="number"
              {...register('jitter_minutes', { valueAsNumber: true })}
              {...props}
            />
          )}
        </Field>
        <Field
          label="Gap cross-plateforme (min)"
          error={errors.cross_platform_min_gap_minutes?.message}
        >
          {(props) => (
            <Input
              type="number"
              {...register('cross_platform_min_gap_minutes', {
                valueAsNumber: true,
              })}
              {...props}
            />
          )}
        </Field>
      </div>

      <Field
        label="Hot hours (JSON)"
        helper={`Format : [{"start":"18:00","end":"22:00","weight":3}]`}
        error={errors.hot_hours_jsonb_text?.message}
      >
        {(props) => (
          <Controller
            control={control}
            name="hot_hours_jsonb_text"
            render={({ field }) => <Textarea {...field} {...props} rows={4} spellCheck={false} />}
          />
        )}
      </Field>

      <div className="platform-pacing-card-footer">
        <Button type="submit" variant="primary" disabled={!isDirty || upsertMutation.isPending}>
          {upsertMutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </div>
    </form>
  )
}
